import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminAPI } from '@/services/api';
import { motion } from 'framer-motion';
import { 
  User,
  Mail,
  Phone,
  Globe,
  Calendar,
  ArrowLeft,
  DollarSign,
  TrendingUp,
  ArrowDownToLine,
  ArrowUpFromLine,
  Edit,
  Ban,
  Trash2,
  Plus,
  Minus
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { format } from 'date-fns';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.3 }
};

export default function AdminUserDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [profitDialogOpen, setProfitDialogOpen] = useState(false);
  const [editData, setEditData] = useState({ status: '', balance: '', tier: '' });
  const [profitData, setProfitData] = useState({ amount: '', is_profit: true, note: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchUser();
  }, [userId]);

  const fetchUser = async () => {
    try {
      const response = await adminAPI.getUser(userId);
      setUserData(response.data);
      setEditData({
        status: response.data.user.status,
        balance: response.data.user.balance.toString(),
        tier: response.data.user.tier || 'Basic'
      });
    } catch (error) {
      console.error('Failed to fetch user:', error);
      toast.error('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    setSaving(true);
    try {
      await adminAPI.updateUser(userId, {
        status: editData.status,
        balance: parseFloat(editData.balance),
        tier: editData.tier
      });
      toast.success('User updated successfully');
      setEditDialogOpen(false);
      fetchUser();
    } catch (error) {
      toast.error('Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleAddProfitLoss = async () => {
    if (!profitData.amount || parseFloat(profitData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    setSaving(true);
    try {
      await adminAPI.manualProfitLoss({
        user_id: userId,
        amount: parseFloat(profitData.amount),
        is_profit: profitData.is_profit,
        note: profitData.note
      });
      toast.success(`${profitData.is_profit ? 'Profit' : 'Loss'} added successfully`);
      setProfitDialogOpen(false);
      setProfitData({ amount: '', is_profit: true, note: '' });
      fetchUser();
    } catch (error) {
      toast.error('Failed to add profit/loss');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }
    try {
      await adminAPI.deleteUser(userId);
      toast.success('User deleted successfully');
      navigate('/admin/users');
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-hotpink-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="text-center py-12">
        <p className="text-white/40">User not found</p>
        <Button onClick={() => navigate('/admin/users')} className="mt-4">
          Back to Users
        </Button>
      </div>
    );
  }

  const { user, deposits, withdrawals, investments } = userData;

  return (
    <motion.div 
      initial="initial"
      animate="animate"
      className="space-y-6"
      data-testid="admin-user-detail-page"
    >
      {/* Header */}
      <motion.div variants={fadeInUp} className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate('/admin/users')}
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-outfit font-bold text-white">{user.name}</h1>
          <p className="text-white/60">{user.email}</p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={profitDialogOpen} onOpenChange={setProfitDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="btn-secondary" data-testid="add-profit-loss-btn">
                <DollarSign className="w-4 h-4 mr-2" />
                Add Profit/Loss
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-space-100 border-white/10">
              <DialogHeader>
                <DialogTitle className="text-white">Add Manual Profit/Loss</DialogTitle>
                <DialogDescription className="text-white/60">
                  Add or deduct amount from user's balance
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setProfitData({ ...profitData, is_profit: true })}
                    className={`p-3 rounded-lg border transition-all ${
                      profitData.is_profit
                        ? 'border-emerald-500 bg-emerald-500/10'
                        : 'border-white/10 hover:border-white/30'
                    }`}
                  >
                    <Plus className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
                    <p className="text-sm text-white">Add Profit</p>
                  </button>
                  <button
                    onClick={() => setProfitData({ ...profitData, is_profit: false })}
                    className={`p-3 rounded-lg border transition-all ${
                      !profitData.is_profit
                        ? 'border-hotpink-500 bg-hotpink-500/10'
                        : 'border-white/10 hover:border-white/30'
                    }`}
                  >
                    <Minus className="w-5 h-5 text-hotpink-400 mx-auto mb-1" />
                    <p className="text-sm text-white">Deduct Loss</p>
                  </button>
                </div>
                <div className="space-y-2">
                  <Label className="text-white/80">Amount (USD)</Label>
                  <Input
                    type="number"
                    value={profitData.amount}
                    onChange={(e) => setProfitData({ ...profitData, amount: e.target.value })}
                    className="input-field"
                    placeholder="Enter amount"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/80">Note (Optional)</Label>
                  <Textarea
                    value={profitData.note}
                    onChange={(e) => setProfitData({ ...profitData, note: e.target.value })}
                    className="input-field"
                    placeholder="Reason for adjustment"
                  />
                </div>
                <Button
                  onClick={handleAddProfitLoss}
                  className="btn-primary w-full"
                  disabled={saving}
                >
                  {saving ? 'Processing...' : 'Apply'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="edit-user-btn">
                <Edit className="w-4 h-4 mr-2" />
                Edit User
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-space-100 border-white/10">
              <DialogHeader>
                <DialogTitle className="text-white">Edit User</DialogTitle>
                <DialogDescription className="text-white/60">
                  Update user details and status
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-white/80">Status</Label>
                  <Select value={editData.status} onValueChange={(v) => setEditData({ ...editData, status: v })}>
                    <SelectTrigger className="input-field">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-space-100 border-white/10">
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="unverified">Unverified</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                      <SelectItem value="banned">Banned</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-white/80">Balance (USD)</Label>
                  <Input
                    type="number"
                    value={editData.balance}
                    onChange={(e) => setEditData({ ...editData, balance: e.target.value })}
                    className="input-field"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/80">Tier</Label>
                  <Select value={editData.tier} onValueChange={(v) => setEditData({ ...editData, tier: v })}>
                    <SelectTrigger className="input-field">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-space-100 border-white/10">
                      <SelectItem value="Basic">Basic</SelectItem>
                      <SelectItem value="Premium">Premium</SelectItem>
                      <SelectItem value="VIP">VIP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={handleUpdateUser}
                  className="btn-primary w-full"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Button 
            variant="outline" 
            className="border-hotpink-400/50 text-hotpink-400 hover:bg-hotpink-400/10"
            onClick={handleDeleteUser}
            data-testid="delete-user-btn"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </motion.div>

      {/* User Info Grid */}
      <div className="grid md:grid-cols-3 gap-6">
        {/* Profile Info */}
        <motion.div variants={fadeInUp} className="glass-card p-6">
          <h3 className="text-lg font-outfit font-semibold text-white mb-4">Profile</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-white/50" />
              <span className="text-white">{user.email}</span>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-white/50" />
              <span className="text-white">{user.phone || 'Not provided'}</span>
            </div>
            <div className="flex items-center gap-3">
              <Globe className="w-5 h-5 text-white/50" />
              <span className="text-white">{user.country || 'Not provided'}</span>
            </div>
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-white/50" />
              <span className="text-white">{format(new Date(user.created_at), 'MMM dd, yyyy')}</span>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-white/10">
            <div className="flex items-center justify-between">
              <span className="text-white/60">Status</span>
              <span className={`status-badge ${
                user.status === 'active' ? 'status-badge-success' :
                user.status === 'unverified' ? 'status-badge-warning' :
                'status-badge-danger'
              }`}>
                {user.status}
              </span>
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-white/60">Tier</span>
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                user.tier === 'VIP' ? 'bg-yellow-500/20 text-yellow-400' :
                user.tier === 'Premium' ? 'bg-cyan-500/20 text-cyan-400' :
                'bg-white/10 text-white/70'
              }`}>
                {user.tier || 'Basic'}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Financial Stats */}
        <motion.div variants={fadeInUp} className="glass-card p-6 md:col-span-2">
          <h3 className="text-lg font-outfit font-semibold text-white mb-4">Financial Overview</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 rounded-lg bg-emerald-500/10">
              <DollarSign className="w-6 h-6 text-emerald-400 mb-2" />
              <p className="text-white/50 text-xs">Balance</p>
              <p className="text-xl font-mono font-bold text-white">${user.balance?.toLocaleString()}</p>
            </div>
            <div className="p-4 rounded-lg bg-cyan-500/10">
              <TrendingUp className="w-6 h-6 text-cyan-400 mb-2" />
              <p className="text-white/50 text-xs">Trading</p>
              <p className="text-xl font-mono font-bold text-white">${user.invested_amount?.toLocaleString()}</p>
            </div>
            <div className="p-4 rounded-lg bg-emerald-500/10">
              <DollarSign className="w-6 h-6 text-emerald-400 mb-2" />
              <p className="text-white/50 text-xs">Total Profit</p>
              <p className="text-xl font-mono font-bold text-emerald-400">+${user.total_profit?.toLocaleString()}</p>
            </div>
            <div className="p-4 rounded-lg bg-yellow-500/10">
              <ArrowDownToLine className="w-6 h-6 text-yellow-400 mb-2" />
              <p className="text-white/50 text-xs">Deposits</p>
              <p className="text-xl font-mono font-bold text-white">{deposits?.length || 0}</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Transactions */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Deposits */}
        <motion.div variants={fadeInUp} className="glass-card p-6">
          <h3 className="text-lg font-outfit font-semibold text-white mb-4">Deposits</h3>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {deposits?.map((deposit) => (
              <div key={deposit.id} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                <div>
                  <p className="text-white font-medium">${deposit.amount.toLocaleString()}</p>
                  <p className="text-white/50 text-xs">{deposit.crypto_type} • {format(new Date(deposit.created_at), 'MMM dd')}</p>
                </div>
                <span className={`status-badge ${
                  deposit.status === 'approved' ? 'status-badge-success' :
                  deposit.status === 'rejected' ? 'status-badge-danger' :
                  'status-badge-warning'
                }`}>
                  {deposit.status}
                </span>
              </div>
            ))}
            {(!deposits || deposits.length === 0) && (
              <p className="text-white/40 text-center py-4">No deposits</p>
            )}
          </div>
        </motion.div>

        {/* Withdrawals */}
        <motion.div variants={fadeInUp} className="glass-card p-6">
          <h3 className="text-lg font-outfit font-semibold text-white mb-4">Withdrawals</h3>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {withdrawals?.map((withdrawal) => (
              <div key={withdrawal.id} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                <div>
                  <p className="text-white font-medium">${withdrawal.amount.toLocaleString()}</p>
                  <p className="text-white/50 text-xs">{withdrawal.crypto_type} • {format(new Date(withdrawal.created_at), 'MMM dd')}</p>
                </div>
                <span className={`status-badge ${
                  withdrawal.status === 'completed' ? 'status-badge-success' :
                  withdrawal.status === 'rejected' ? 'status-badge-danger' :
                  'status-badge-warning'
                }`}>
                  {withdrawal.status}
                </span>
              </div>
            ))}
            {(!withdrawals || withdrawals.length === 0) && (
              <p className="text-white/40 text-center py-4">No withdrawals</p>
            )}
          </div>
        </motion.div>
      </div>

      {/* Trading Positions */}
      <motion.div variants={fadeInUp} className="glass-card p-6">
        <h3 className="text-lg font-outfit font-semibold text-white mb-4">Trading Positions</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-white/50 text-sm border-b border-white/10">
                <th className="pb-3 font-medium">Amount</th>
                <th className="pb-3 font-medium">Daily Rate</th>
                <th className="pb-3 font-medium">Total Profit</th>
                <th className="pb-3 font-medium">Status</th>
                <th className="pb-3 font-medium">Created</th>
              </tr>
            </thead>
            <tbody>
              {investments?.map((investment) => (
                <tr key={investment.id} className="table-row">
                  <td className="py-3 font-mono text-white">${investment.amount.toLocaleString()}</td>
                  <td className="py-3 text-cyan-400">{investment.daily_rate}%</td>
                  <td className="py-3 font-mono text-emerald-400">+${(investment.total_profit || 0).toFixed(2)}</td>
                  <td className="py-3">
                    <span className={`status-badge ${
                      investment.status === 'active' ? 'status-badge-success' : 'status-badge-warning'
                    }`}>
                      {investment.status}
                    </span>
                  </td>
                  <td className="py-3 text-white/50 text-sm">
                    {format(new Date(investment.created_at), 'MMM dd, yyyy')}
                  </td>
                </tr>
              ))}
              {(!investments || investments.length === 0) && (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-white/40">
                    No investments
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </motion.div>
    </motion.div>
  );
}
